import {
  makeValueGetter,
  withPendingValueGetter,
} from "@platform/design-system";
import {
  dollarSavingsRowLabel,
  totalDollarSavingsRowLabel,
  totalVarianceRowLabel,
  varianceRowLabel,
} from "@platform/formatting-utils/financials";

import { resolveTotalsBlockName } from "../totalsBlockName";

import {
  CARRIER_COL_WIDTH_PX,
  DATA_COL_MAX_WIDTH_PX,
  PERIOD_COL_WIDTH_PX,
} from "./constants";
import { financialsCellRendererSelector } from "./cells";
import { CreditsLabelCellRenderer } from "./cells/CreditsLabelCellRenderer";
import { NotesLabelCellRenderer } from "./cells/NotesLabelCellRenderer";
import { getCellValue, lookupBlockCoverageIds } from "./valueGetters";
import { getFinancialsNotesCellStyle } from "./notesCellStyle";
import { financialsRowKinds } from "./types";

import type {
  FinancialsGridContext,
  FinancialsRow,
  FinancialsRowKind,
} from "./types";
import type {
  CarrierColumn,
  FinancialsTableResponse,
  PeriodKey,
} from "@platform/api";
import type {
  CellEditorSelectorResult,
  ColDef,
  EditableCallbackParams,
  ICellEditorParams,
  ValueGetterParams,
} from "ag-grid-community";

const PERIOD_DISPLAY_NAMES: Record<PeriodKey, string> = {
  current: "Current",
  renewal: "Renewal",
  revisedRenewal: "Revised Renewal",
};

const PERIOD_ORDER: PeriodKey[] = ["current", "renewal", "revisedRenewal"];

/**
 * Labels that don't depend on payload state. Dynamic labels (variance,
 * dollar savings, totals variance/savings, coverage banner) fall
 * through to `resolveDynamicLabel`.
 */
const STATIC_ROW_LABELS: Partial<Record<FinancialsRowKind, string>> = {
  [financialsRowKinds.ANNUAL_PREMIUM]: "Annual Premium",
  [financialsRowKinds.TOTAL_ANNUAL_PREMIUM]: "Total Annual Premium",
  [financialsRowKinds.TOTAL_PREMIUM_WITH_CREDITS]:
    "Total Annual Premium with Credits Applied",
};

const resolveTotalsHeaderLabel = (
  context: FinancialsGridContext,
  row: FinancialsRow,
): string =>
  resolveTotalsBlockName(context.blockById.get(row.blockId ?? "default"));

const resolveDynamicLabel = (
  context: FinancialsGridContext,
  row: FinancialsRow,
): string => {
  if (row.kind === financialsRowKinds.TOTALS_HEADER) {
    return resolveTotalsHeaderLabel(context, row);
  }

  if (row.kind === financialsRowKinds.TOTAL_VARIANCE) {
    return totalVarianceRowLabel(
      context.payload,
      lookupBlockCoverageIds(context, row),
    );
  }
  if (row.kind === financialsRowKinds.TOTAL_DOLLAR_SAVINGS) {
    return totalDollarSavingsRowLabel(
      context.payload,
      lookupBlockCoverageIds(context, row),
    );
  }
  if (row.kind === financialsRowKinds.TOTAL_CREDITS) {
    const block = context.blockById.get(row.blockId ?? "default");
    return block?.creditsRow.label ?? "";
  }
  if (row.kind === financialsRowKinds.TOTAL_NOTES) {
    const label = context.blockById
      .get(row.blockId ?? "default")
      ?.notesRowHeaderLabel?.trim();
    return label ? label : "Notes";
  }

  const coverageId = context.coverageByRowId.get(row.rowId);
  if (coverageId === undefined) return "";

  if (row.kind === financialsRowKinds.NOTES) {
    const label = context.coverageById
      .get(coverageId)
      ?.notesRowHeaderLabel?.trim();
    return label ? label : "Notes";
  }

  if (row.kind === financialsRowKinds.VARIANCE) {
    return varianceRowLabel(context.payload, coverageId);
  }
  if (row.kind === financialsRowKinds.DOLLAR_SAVINGS) {
    return dollarSavingsRowLabel(context.payload, coverageId);
  }

  return "";
};

const resolveLabel = (
  context: FinancialsGridContext,
  row: FinancialsRow,
): string => STATIC_ROW_LABELS[row.kind] ?? resolveDynamicLabel(context, row);

const CHILD_ROW_KINDS: ReadonlySet<string> = new Set([
  financialsRowKinds.ANNUAL_PREMIUM,
  financialsRowKinds.VARIANCE,
  financialsRowKinds.DOLLAR_SAVINGS,
  financialsRowKinds.NOTES,
  financialsRowKinds.TOTAL_ANNUAL_PREMIUM,
  financialsRowKinds.TOTAL_VARIANCE,
  financialsRowKinds.TOTAL_DOLLAR_SAVINGS,
  financialsRowKinds.TOTAL_NOTES,
  financialsRowKinds.TOTAL_CREDITS,
  financialsRowKinds.TOTAL_PREMIUM_WITH_CREDITS,
]);

const isChildRow = (row: FinancialsRow): boolean =>
  CHILD_ROW_KINDS.has(row.kind);

const EDITABLE_KINDS: ReadonlySet<FinancialsRowKind> = new Set([
  financialsRowKinds.ANNUAL_PREMIUM,
  financialsRowKinds.NOTES,
  financialsRowKinds.TOTAL_NOTES,
  financialsRowKinds.TOTAL_CREDITS,
]);

const isEditable = (params: EditableCallbackParams<FinancialsRow>): boolean => {
  if (!params.data) return false;

  if (params.data.kind === financialsRowKinds.ANNUAL_PREMIUM) {
    return (params.context as FinancialsGridContext).premiumOverridesEnabled;
  }

  return EDITABLE_KINDS.has(params.data.kind);
};

const NOTE_KINDS: ReadonlySet<FinancialsRowKind> = new Set([
  financialsRowKinds.NOTES,
  financialsRowKinds.TOTAL_NOTES,
]);

const NOTE_EDITOR_PARAMS = {
  maxLength: 1024,
  rows: 5,
  cols: 50,
};

const cellEditorSelector = (
  params: ICellEditorParams<FinancialsRow>,
): CellEditorSelectorResult | undefined => {
  if (!params.data) return undefined;

  if (NOTE_KINDS.has(params.data.kind)) {
    return {
      component: "agLargeTextCellEditor",
      params: NOTE_EDITOR_PARAMS,
      popup: true,
    };
  }

  if (
    params.data.kind === financialsRowKinds.ANNUAL_PREMIUM ||
    params.data.kind === financialsRowKinds.TOTAL_CREDITS
  ) {
    return { component: "agNumberCellEditor" };
  }

  return undefined;
};

const buildLabelColDef = (): ColDef<FinancialsRow> => ({
  colId: "label",
  headerName: "",
  pinned: "left",
  lockPinned: true,
  suppressMovable: true,
  resizable: false,
  width: 240,
  wrapText: true,
  autoHeight: true,
  valueGetter: (params: ValueGetterParams<FinancialsRow>): string => {
    if (!params.data) return "";
    const context = params.context as FinancialsGridContext;

    return resolveLabel(context, params.data);
  },
  cellStyle: (params) =>
    params.data && isChildRow(params.data)
      ? { paddingLeft: "2rem" }
      : undefined,
  cellRendererSelector: (params) => {
    if (params.data?.kind === financialsRowKinds.TOTAL_CREDITS) {
      return { component: CreditsLabelCellRenderer };
    }
    if (
      params.data?.kind === financialsRowKinds.NOTES ||
      params.data?.kind === financialsRowKinds.TOTAL_NOTES
    ) {
      return { component: NotesLabelCellRenderer };
    }
    return undefined;
  },
});

const buildPeriodColDef = (
  period: PeriodKey,
  hidden: boolean,
): ColDef<FinancialsRow> => ({
  colId: `period:${period}`,
  headerName: PERIOD_DISPLAY_NAMES[period],
  hide: hidden,
  width: PERIOD_COL_WIDTH_PX,
  wrapText: true,
  autoHeight: true,
  cellStyle: (params) => getFinancialsNotesCellStyle(params.data?.kind),
  suppressMovable: true,
  editable: isEditable,
  cellEditorSelector,
  valueSetter: () => true,
  valueGetter: withPendingValueGetter(
    makeValueGetter({ kind: "period", period }, getCellValue),
  ),
  cellRendererSelector: financialsCellRendererSelector,
});

const buildCarrierColDef = (carrier: CarrierColumn): ColDef<FinancialsRow> => ({
  colId: `carrier:${carrier.id}`,
  headerName: carrier.name,
  hide: carrier.hidden,
  flex: 1,
  minWidth: CARRIER_COL_WIDTH_PX,
  maxWidth: DATA_COL_MAX_WIDTH_PX,
  wrapText: true,
  autoHeight: true,
  // Carrier order here is fixed to the sequence applied from the Quotes tab
  // (CD-1806) — dragging on this page was never persisted or propagated, and
  // being movable let a carrier drag visually displace the period columns
  // even though those already set suppressMovable themselves.
  suppressMovable: true,
  cellStyle: (params) => getFinancialsNotesCellStyle(params.data?.kind),
  editable: isEditable,
  cellEditorSelector,
  valueSetter: () => true,
  valueGetter: withPendingValueGetter(
    makeValueGetter({ kind: "carrier", carrierId: carrier.id }, getCellValue),
  ),
  cellRendererSelector: financialsCellRendererSelector,
});

const sortByOrder = <T extends { order: number }>(a: T, b: T): number =>
  a.order - b.order;

/**
 * Builds AG Grid column defs from the payload. Order: pinned-left
 * label column, then the three period columns in fixed order
 * (Current → Renewal → Revised Renewal regardless of `order`), then
 * carrier columns sorted by `order` ascending.
 *
 * Per-column `hide` reflects the payload's persisted visibility for
 * that period or carrier; cell-level rendering and value resolution
 * are wired through `valueGetters` and (later) `cells`.
 */
export const buildFinancialsColDefs = (
  payload: FinancialsTableResponse,
): ColDef<FinancialsRow>[] => [
  buildLabelColDef(),
  ...PERIOD_ORDER.map((period) =>
    buildPeriodColDef(period, payload.periods[period].hidden),
  ),
  ...[...payload.carriers].sort(sortByOrder).map(buildCarrierColDef),
];
